import useBlockedUsers from '@contexts/useBlockedUsers';
import {
  QueryKeys,
  assertParameterIsNotEmptyString,
  assertParameterIsNotUndefinedOrNull,
  connectionsApi,
  useAuthMutation,
  useAuthQuery,
} from '@sunrise-ui/api-client';
import { type ConnectionsApiGetConnectionsConnectionsUserIdGetRequest } from '@sunrise-ui/api/network';
import { useAuthStore } from '@sunrise-ui/primitives';
import { useMemo } from 'react';

const { myConnectionsKey, isConnectedKey } = QueryKeys.connections;

export const useGetConnections = ({
  userId,
  enabled = true,
}: ConnectionsApiGetConnectionsConnectionsUserIdGetRequest & {
  enabled?: boolean;
}) => {
  const { isLoading: isLoadingBlockedUsers, isUserBlocked } = useBlockedUsers();

  const {
    isLoading: isLoadingConnections,
    data,
    ...otherProps
  } = useAuthQuery({
    async queryFn() {
      const response = await connectionsApi.getConnectionsConnectionsUserIdGet({
        userId,
      });
      return response.data;
    },
    queryKey: [myConnectionsKey, userId],
    enabled: Boolean(userId) && enabled && !isLoadingBlockedUsers,
  });

  const filteredData = useMemo(
    () => data?.filter((user) => !isUserBlocked(user.user_id)),
    [data, isUserBlocked],
  );

  return {
    isLoading: isLoadingConnections || isLoadingBlockedUsers,
    data: filteredData,
    ...otherProps,
  };
};

export const useIsConnected = ({ userId }: { userId?: string }) => {
  const { currentUserId } = useAuthStore();
  return useAuthQuery({
    async queryFn() {
      assertParameterIsNotEmptyString(userId, 'userId');
      assertParameterIsNotUndefinedOrNull(userId, 'userId');
      assertParameterIsNotUndefinedOrNull(currentUserId, 'currentUserId');

      const response =
        await connectionsApi.getConnectionStrengthConnectionsConnectionStrengthGet(
          {
            user1Id: currentUserId,
            user2Id: userId,
          },
        );
      return response.data.connection_count > 0;
    },
    queryKey: [isConnectedKey, userId],
    enabled: Boolean(userId),
  });
};

export const useGetIsConnected = () => {
  return useAuthMutation({
    async mutationFn({ userId }: { userId: string }) {
      const { currentUserId } = useAuthStore.getState();
      assertParameterIsNotUndefinedOrNull(currentUserId, 'currentUserId');
      const response =
        await connectionsApi.getConnectionStrengthConnectionsConnectionStrengthGet(
          {
            user1Id: currentUserId,
            user2Id: userId,
          },
        );
      return response.data.connection_count > 0;
    },
  });
};
